import { AsyncPipe, NgClass, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { VaFilterModule } from '@vendasta/uikit';
import { BehaviorSubject, Observable, ReplaySubject, startWith, Subject, take, takeUntil } from 'rxjs';
import { VaListItemComponent } from '../common/va-list-item';
import { AccountService } from '../core/account.service';
import { User } from '../core/user';
import { AssociationsApiService } from '../user-permissions/associations.api.service';
import { UserService } from '../users';
import { CreateUserFormComponent } from '../users/user/create-user-form.component';

export enum UserAction {
  ADD = 'add',
  CREATE = 'create',
}
export interface AddUserResponse {
  action: UserAction;
  users: User[];
}
@Component({
  styleUrls: ['add-user-dialog.component.scss', '../common/table.scss'],
  templateUrl: './add-user-dialog.component.html',
  providers: [AssociationsApiService],
  standalone: true,
  imports: [
    AsyncPipe,
    CreateUserFormComponent,
    GalaxyButtonLoadingIndicatorModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    NgClass,
    NgForOf,
    NgIf,
    NgTemplateOutlet,
    TranslateModule,
    VaFilterModule,
    VaListItemComponent,
  ],
})
export class AddUserDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() accountGroupId = '';
  @Input() partnerId = '';
  @Input() existingUsers: User[] = [];

  @Input() brandView = false;
  @Input() showCreateTab = false;
  @Input() brandPath = '';

  @ViewChild('createForm') createFormComponent: CreateUserFormComponent;

  term$ = new Subject<string>();
  users$: Observable<User[]>;
  loading$: Observable<boolean>;

  selectedUsers: User[] = [];
  addedUsers: User[] = [];
  loadingUsers: User[] = [];

  private isLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoading$$.asObservable();

  private tab$$: ReplaySubject<UserAction> = new ReplaySubject<UserAction>(1);
  tab$ = this.tab$$.asObservable();

  private _destroy$$ = new Subject<void>();
  UserAction = UserAction;

  constructor(
    public userService: UserService,
    private accountService: AccountService,
    public dialogRef: MatDialogRef<AddUserDialogComponent, AddUserResponse | string | User[]>,
    private associationsApiService: AssociationsApiService,
    private snackbar: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.loading$ = this.userService.isLoading$;
    this.users$ = this.userService.search(this.term$.pipe(startWith('')));
    this.tab$$.next(this.showCreateTab ? UserAction.CREATE : UserAction.ADD);
  }

  ngAfterViewInit(): void {
    if (this.showCreateTab) {
      this.createFormComponent
        .isCreatingUser()
        .pipe(takeUntil(this._destroy$$))
        .subscribe((isLoading) => this.isLoading$$.next(isLoading));
    }
  }

  ngOnDestroy(): void {
    this._destroy$$.next();
    this._destroy$$.complete();
  }

  addUser(user: User): void {
    if (!this.isUserExists(user)) {
      this.accountService.addUserToAccount(this.accountGroupId, user);
      this.addedUsers.push(user);
    }
  }

  addSelectedUsers(): void {
    this.isLoading$$.next(true);
    const usersToAdd: User[] = [];
    for (const user of this.selectedUsers) {
      if (!this.isUserExists(user)) {
        usersToAdd.push(user);
      }
    }
    if (usersToAdd.length > 0) {
      this.addedUsers.push(...usersToAdd);
      this.associateUsersWithAccount(usersToAdd, UserAction.ADD);
    }
  }

  addUserToSelected(event: MatCheckboxChange, user: User): void {
    if (event.checked) {
      this.selectedUsers.push(user);
    } else {
      const index = this.selectedUsers.indexOf(user, 0);
      if (index > -1) {
        this.selectedUsers.splice(index, 1);
      }
    }
  }

  addUserToBrand(user: User): void {
    this.loadingUsers.push(user);
    this.associationsApiService.addBrandAssocation(user.userId, this.brandPath).subscribe({
      next: () => {
        this.loadingUsers = this.loadingUsers.filter((u) => u.userId !== user.userId);
        this.addedUsers.push(user);
        this.snackbar.openSuccessSnack('Successfully added user');
      },
      error: () => {
        this.loadingUsers = this.loadingUsers.filter((u) => u.userId !== user.userId);
        this.snackbar.openErrorSnack('Error adding user');
      },
    });
  }

  isUserExists(user: User): boolean {
    if (!this.existingUsers) {
      return false;
    }
    return (
      [...this.addedUsers, ...this.existingUsers].filter((existingUser) => existingUser.userId === user.userId).length >
      0
    );
  }

  isLoading(user: User): boolean {
    return this.loadingUsers.filter((existingUser) => existingUser.userId === user.userId).length > 0;
  }

  customAccess(user: User): void {
    this.dialogRef.close(user.userId);
  }

  userCreated(user: User): void {
    this.associateUsersWithAccount([user], UserAction.CREATE);
  }

  tabChanged(event: MatTabChangeEvent): void {
    this.tab$$.next(event.index === 0 ? UserAction.CREATE : UserAction.ADD);
  }

  createUser(): void {
    this.createFormComponent.createUser();
  }

  onSubmit(): void {
    this.tab$.pipe(take(1)).subscribe((tab) => (tab === UserAction.ADD ? this.addSelectedUsers() : this.createUser()));
  }

  private associateUsersWithAccount(users: User[], action: UserAction): void {
    this.accountService
      .addUsersToAccount(this.partnerId, this.accountGroupId, users)
      .pipe(take(1))
      .subscribe({
        next: () => this.dialogRef.close({ action: action, users }),
        error: () => this.dialogRef.close(),
        complete: () => this.isLoading$$.next(false),
      });
  }
}
